import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RouterConstant } from "../../routers/index";
import { LessonService } from "../../services/lessons/lesson.service";
import { useLayoutContext } from "../../layouts/general-layout";
import { showErrorToast } from "../../components/Toaster/index";
import { SearchSelectionBoard } from "../../components/search-selection-board";

export default function SearchLesson() {
  const navigate = useNavigate();
  const { ROUTERS_PATH } = RouterConstant;
  const { t } = useLayoutContext();

  const [levels, setLevels] = useState<{ id: number; name: string; code: number }[]>([]);
  const [topics, setTopics] = useState<{ id: number; name: string; code: number }[]>([]);
  const [subTopics, setSubTopics] = useState<{ id: number; name: string; code: number }[]>([]);
  const [isLevelsLoading, setIsLevelsLoading] = useState<boolean>(false);
  const [isTopicsLoading, setIsTopicsLoading] = useState<boolean>(false);
  const [isSubTopicsLoading, setIsSubTopicsLoading] = useState<boolean>(false);
  
  const [selectedLevel, setSelectedLevel] = useState<number | null>(null);
  const [selectedTopic, setSelectedTopic] = useState<number | null>(null);
  const [selectedSubTopic, setSelectedSubTopic] = useState<number | null>(null);

  const lessonService = new LessonService();
  const sortByCode = (data: any[]) => data.sort((a, b) => a.code - b.code);

  const fetchLevels = async () => {
    setIsLevelsLoading(true);
    try {
      const levelsData = await lessonService.getLessonLevels();
      const sortedLevels = sortByCode(levelsData);
      setLevels(sortedLevels);
      if (sortedLevels.length > 0) {
        const defaultLevel = sortedLevels[0];
        setSelectedLevel(defaultLevel.id);
        fetchTopics(defaultLevel.id);
      } else {
        setSelectedLevel(null);
        setTopics([]);
        setSubTopics([]);
      }
    } catch (error) {
      console.error("Error fetching levels:", error);
    } finally {
      setIsLevelsLoading(false);
    }
  };

  const fetchTopics = async (levelId: number) => {
    setIsTopicsLoading(true);
    try {
      const topicsData = await lessonService.getLessonTopics(levelId);
      const sortedTopics = sortByCode(topicsData);
      setTopics(sortedTopics);
      if (sortedTopics.length > 0) {
        const defaultTopic = sortedTopics[0];
        setSelectedTopic(defaultTopic.id);
        fetchSubTopics(levelId, defaultTopic.id);
      } else {
        setSelectedTopic(null);
        setSubTopics([]);
        setSelectedSubTopic(null);
      }
    } catch (error) {
      console.error("Error fetching topics:", error);
    } finally {
      setIsTopicsLoading(false);
    }
  };

  const fetchSubTopics = async (levelId: number, topicId: number) => {
    setIsSubTopicsLoading(true);
    try {
      const subTopicsData = await lessonService.getLessonSubTopics(levelId, topicId);
      const sortedSubTopics = sortByCode(subTopicsData || []);
      setSubTopics(sortedSubTopics);
      if (sortedSubTopics.length > 0) {
        const defaultSubTopic = sortedSubTopics[0];
        setSelectedSubTopic(defaultSubTopic.id);
      } else {
        setSelectedSubTopic(null);
      }
    } catch (error) {
      console.error("Error fetching sub topics:", error);
    } finally {
      setIsSubTopicsLoading(false);
    }
  };

  useEffect(() => {
    fetchLevels();
  }, []);

  const handleSearch = () => {
    if (!selectedLevel || !selectedTopic) {
      showErrorToast(t("pleaseSelectLevelAndTopic")); // Use localized string if available, or keep hebrew for now if not in plan
      return;
    }

    try {
      navigate(ROUTERS_PATH.PLAY_LESSON, {
        state: {
          levelId: selectedLevel,
          topicId: selectedTopic,
          subTopicId: selectedSubTopic,
          levelName: levels.find(l => l.id === selectedLevel)?.name || '',
          topicName: topics.find(t => t.id === selectedTopic)?.name || '',
          subTopicName: subTopics.find(s => s.id === selectedSubTopic)?.name || '',
          from: "searchLesson",
        },
      });
    } catch (error) {
      console.error("Error opening lesson:", error);
    }
  };

  const handleBackClick = () => {
    navigate(ROUTERS_PATH.USER_MAIN_DASHBOARD);
  };

  const handleLevelSelect = (id: number) => {
    setSelectedLevel(id);
    fetchTopics(id);
  };

  const handleTopicSelect = (id: number) => {
    setSelectedTopic(id);
    if (selectedLevel) fetchSubTopics(selectedLevel, id);
  };

  return (
    <SearchSelectionBoard
      pageTitle={t("searchLessonTitle")}
      onBackClick={handleBackClick}
      col1Title={t("levelSelection")}
      col2Title={t("chooseTopic")}
      col3Title={t("chooseSubTopic")}
      col1Items={levels}
      col2Items={topics}
      col3Items={subTopics}
      selectedCol1={selectedLevel}
      selectedCol2={selectedTopic}
      selectedCol3={selectedSubTopic}
      onCol1Select={handleLevelSelect}
      onCol2Select={handleTopicSelect}
      onCol3Select={(id) => setSelectedSubTopic(id)}
      isLoadingCol1={isLevelsLoading}
      isLoadingCol2={isTopicsLoading}
      isLoadingCol3={isSubTopicsLoading}
      submitButtonText={t("presentALesson")}
      onSubmit={handleSearch}
    />
  );
}
